import Reveal from "@/components/Reveal";
import { TID } from "@/lib/testIds";
import { ArrowUpRight, Sparkles, Users, Crown } from "lucide-react";
import { useSiteContent, useLinks, resolveImg } from "@/admin/SiteContent";

const CHARACTER_PNG =
  "https://customer-assets.emergentagent.com/job_pure-rp-gaming/artifacts/pm778ikj_Gemini_Generated_Image_qqiokaqqiokaqqio__1_-removebg.png";

export const CharacterShowcase = () => {
  const { content } = useSiteContent();
  const c = content?.character || {};
  const lk = useLinks();
  const characterImg = resolveImg(c.image_url) || CHARACTER_PNG;
  return (
    <section
      id="character"
      data-testid="character-showcase-root"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      {/* Background glows */}
      <div
        className="orb bg-[#00D4FF]"
        style={{ width: 500, height: 500, left: "-10%", top: "10%", opacity: 0.14 }}
      />
      <div
        className="orb bg-[#D4AF37]"
        style={{ width: 400, height: 400, right: "10%", top: "30%", opacity: 0.12 }}
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left content */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#D4AF37]/60" />
                <span className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                  {c.kicker || "Background PG"}
                </span>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-[#F5F5F5] leading-[0.98] tracking-wide">
                {c.title_lead || "Richiedi ora l'accesso alla"}{" "}
                <span className="text-gold glow-gold">{c.title_accent || "Whitelist"}</span>{" "}
                e compila il <span className="text-[#00D4FF]">Background</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-7 text-[#B6B6BE] text-base sm:text-lg leading-relaxed max-w-xl">
                {c.description ||
                  "Pensato per roleplayer, creator e streamer che cercano un server da vivere e raccontare. La tua storia inizia con un personaggio che ha qualcosa da dire."}
              </p>
            </Reveal>

            <Reveal delay={3} className="mt-9 flex flex-wrap gap-3">
              <a
                href={lk.whitelist}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="character-cta-whitelist"
                className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wider"
              >
                {c.cta_label || "Compila il BG"}
                <ArrowUpRight size={16} strokeWidth={2.6} />
              </a>
              <a
                href="#faq"
                className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              >
                Regolamento
              </a>
            </Reveal>

            <Reveal delay={4} className="mt-12 max-w-md">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] mb-4">
                I nostri canali — seguici sulle piattaforme ufficiali
              </p>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={lk.discord}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-wow rounded-xl px-4 py-3 flex items-center gap-3"
                >
                  <span className="w-8 h-8 rounded-lg bg-[#5865F2]/20 border border-[#5865F2]/40 flex items-center justify-center">
                    <Users size={14} className="text-[#7c87ff]" />
                  </span>
                  <span className="text-sm font-semibold text-[#F5F5F5]">Discord</span>
                </a>
                <a
                  href={lk.join}
                  className="card-wow rounded-xl px-4 py-3 flex items-center gap-3"
                >
                  <span className="w-8 h-8 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
                    <Crown size={14} className="text-[#D4AF37]" />
                  </span>
                  <span className="text-sm font-semibold text-[#F5F5F5]">FiveM</span>
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right: Character — wrapper trasparente, NIENTE background */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Reveal delay={1}>
              <div className="relative w-full h-[600px] bg-transparent border-none shadow-none flex items-end justify-center overflow-visible">
                {/* Badge — alto-sinistra, lontano dal personaggio */}
                <div
                  className="absolute top-0 left-0 status-pill border-[#D4AF37]/50 text-[#D4AF37] z-20"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(212,175,55,0.18), rgba(212,175,55,0.06))",
                    boxShadow: "0 0 22px -8px rgba(212,175,55,0.7)",
                  }}
                >
                  <Sparkles size={12} />
                  <span>Personaggio Whitelist</span>
                </div>

                {/* Personaggio */}
                <img
                  src={characterImg}
                  alt="Personaggio PureLife"
                  loading="lazy"
                  className="absolute bottom-0 object-contain drop-shadow-[0_0_40px_rgba(212,175,55,0.8)] pointer-events-none select-none float-y z-10"
                  style={{
                    transform: "scale(1.15)",
                    transformOrigin: "bottom center",
                    maxWidth: "none",
                    height: "100%",
                    maskImage:
                      "linear-gradient(to bottom, black 70%, transparent 100%)",
                    WebkitMaskImage:
                      "linear-gradient(to bottom, black 70%, transparent 100%)",
                  }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CharacterShowcase;
